import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Mic, Users, ChevronRight, CheckCircle2, Star } from "lucide-react";
import { SPEAKING_TOPICS, BRAND } from "../data/content";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_SIZES = [
  "Under 50",
  "50 – 200",
  "200 – 500",
  "500 – 1,000",
  "1,000+",
];

const FORMAT_LABELS = {
  Keynote: "Keynote",
  Panel: "Panel",
  Workshop: "Workshop",
  "Internal leadership session": "Internal session",
};

const initialForm = {
  full_name: "",
  organisation: "",
  email: "",
  event: "",
  event_date: "",
  location: "",
  audience_size: "",
  topic_id: "",
  message: "",
};

export default function Speaking() {
  useDocumentMeta({
    title: "Speaking · Keynotes & workshops on resilient programs — JJ Argent",
    description:
      "Keynotes and workshops on resilient programs, scaling events and practical AI. Signature talk: Built to Run Without Me.",
    path: "/speaking",
    noindex: true, // Batch 2 §10A — unpublished but route retained
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const topic = SPEAKING_TOPICS.find((t) => t.id === form.topic_id);
    // Concatenate speaking-specific fields into the message so they land in the admin inbox
    // in a scannable format — while still using the existing Enquiry model.
    const messageBody = [
      `Speaking enquiry`,
      form.event && `Event: ${form.event}`,
      topic && `Requested topic: ${topic.title}`,
      form.audience_size && `Audience size: ${form.audience_size}`,
      form.event_date && `Preferred date: ${form.event_date}`,
      form.message && ``,
      form.message && form.message,
    ].filter(Boolean).join("\n");

    try {
      await axios.post(`${API}/enquiries`, {
        full_name: form.full_name,
        email: form.email,
        organisation: form.organisation,
        country: form.location,       // reuse country → location
        timeframe: form.event_date,   // reuse timeframe → preferred date
        enquiry_type: "speaking",
        message: messageBody || "Speaking enquiry",
        consent: true,
      });
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Please try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main" className="bg-[#0B0B0C] text-[#FAF9F6]" data-testid="speaking-page">
      {/* Hero */}
      <section className="pt-40 pb-20 relative grain">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow">Speaking</p>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-[-0.04em]">
              Ideas built from{" "}<br />
              <span className="text-[#D8A936]">experience, not theory.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[#A9A9A6]">
              Talks, panels and workshops drawn from twelve years of running programs, events and operations
              at real scale — in universities, festivals and community organisations across Australia and Canada.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <a href="#enquire" data-testid="speaking-hero-enquire" className="btn-primary">
              Enquire about a talk <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Topics */}
      <section className="border-t border-[#343437] py-24" data-testid="speaking-topics">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-7">
              <p className="eyebrow">Topics</p>
              <h2 className="mt-4 font-display text-5xl md:text-6xl leading-[0.98] tracking-tight">
                Seven talks{" "}<br />
                <span className="text-[#D8A936]">shaped by the work.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:pt-14">
              <p className="text-[#A9A9A6] max-w-md leading-relaxed">
                Every topic can be delivered as a keynote, panel, workshop or internal leadership session.
                Length, framing and case studies tailored to your audience.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SPEAKING_TOPICS.map((t, i) => (
              <motion.article
                key={t.id}
                initial={{ y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 2) * 0.06 }}
                className={`border p-8 lg:p-10 flex flex-col hover:bg-[#151517] transition-colors duration-500 ${
                  t.signature ? "border-[#D8A936] bg-[#151517]" : "border-[#343437] bg-[#0B0B0C]"
                }`}
                data-testid={`speaking-topic-${t.id}`}
              >
                <div className="flex items-start justify-between">
                  <span className={`oversize text-4xl ${t.signature ? "text-[#D8A936]" : "text-[#A9A9A6]"}`}>{t.n}</span>
                  {t.signature && (
                    <span className="flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#D8A936]">
                      <Star size={12} /> Signature talk
                    </span>
                  )}
                </div>
                <h3 className="mt-8 font-display text-2xl md:text-3xl tracking-tight text-[#FAF9F6] leading-tight">
                  {t.title}
                </h3>
                <p className="mt-4 text-[#A9A9A6] leading-relaxed flex-1">{t.body}</p>

                <div className="mt-8 pt-6 border-t border-[#343437] space-y-4">
                  <div className="flex items-start gap-3">
                    <Users size={14} className="text-[#D8A936] mt-1 shrink-0" />
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">Audience</p>
                      <p className="text-sm text-[#FAF9F6] mt-1">{t.audience}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Mic size={14} className="text-[#D8A936] mt-1 shrink-0" />
                    <div className="flex-1">
                      <p className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6]">Formats</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {t.formats.map((f) => (
                          <span key={f} className="text-[10px] tracking-[0.16em] uppercase text-[#D8A936] border border-[#D8A936]/40 px-2 py-1">
                            {FORMAT_LABELS[f] || f}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    update("topic_id", t.id);
                    document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid={`speaking-topic-cta-${t.id}`}
                  className="mt-8 inline-flex items-center gap-2 font-display text-[12px] tracking-[0.18em] uppercase text-[#D8A936] hover:text-[#FAF9F6] transition-colors self-start"
                >
                  Enquire about this talk <ChevronRight size={14} />
                </button>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquire" className="border-t border-[#343437] py-24 bg-[#151517] scroll-mt-24" data-testid="speaking-enquiry">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">Speaking enquiry</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02] tracking-tight">
              Bring me in{" "}<br />
              <span className="text-[#D8A936]">for your event.</span>
            </h2>
            <p className="mt-6 text-[#A9A9A6] leading-relaxed max-w-md">
              Send the event details. I'll respond within two business days with availability,
              proposed framing and a suggested format.
            </p>

            <div className="mt-10 border border-[#343437] p-6 bg-[#0B0B0C]">
              <p className="eyebrow">Also open to</p>
              <ul className="mt-4 space-y-2 text-sm text-[#A9A9A6]">
                <li>· Podcast interviews</li>
                <li>· Panel appearances</li>
                <li>· Executive team sessions</li>
                <li>· Recorded fireside chats</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="border border-[#D8A936] p-10 md:p-12 bg-[#0B0B0C] flex flex-col items-start gap-6" data-testid="speaking-success">
                <CheckCircle2 size={40} className="text-[#D8A936]" />
                <h3 className="font-display text-3xl leading-tight tracking-tight">Enquiry received.</h3>
                <p className="text-[#A9A9A6] max-w-lg">
                  Thanks — I'll reply within two business days with availability and a proposed framing
                  for your event.
                </p>
                <div className="flex flex-wrap gap-3">
                  <a href={BRAND.linkedin} target="_blank" rel="noopener noreferrer" className="btn-primary">
                    Connect on LinkedIn <ArrowUpRight size={14} />
                  </a>
                  <button
                    type="button"
                    onClick={() => { setForm(initialForm); setDone(false); }}
                    data-testid="speaking-send-another"
                    className="btn-ghost"
                  >
                    Send another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="speaking-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SpField label="Full name" required>
                    <input type="text" required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="sp-input" data-testid="sp-name" />
                  </SpField>
                  <SpField label="Organisation" required>
                    <input type="text" required value={form.organisation} onChange={(e) => update("organisation", e.target.value)} className="sp-input" data-testid="sp-org" />
                  </SpField>
                </div>
                <SpField label="Email" required>
                  <input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className="sp-input" data-testid="sp-email" />
                </SpField>
                <SpField label="Event name" required>
                  <input type="text" required value={form.event} onChange={(e) => update("event", e.target.value)} className="sp-input" placeholder="e.g. UMSU Leadership Retreat 2026" data-testid="sp-event" />
                </SpField>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SpField label="Event date">
                    <input type="date" value={form.event_date} onChange={(e) => update("event_date", e.target.value)} className="sp-input" data-testid="sp-date" />
                  </SpField>
                  <SpField label="Location">
                    <input type="text" value={form.location} onChange={(e) => update("location", e.target.value)} className="sp-input" placeholder="City · Country · Online" data-testid="sp-location" />
                  </SpField>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SpField label="Audience size">
                    <select value={form.audience_size} onChange={(e) => update("audience_size", e.target.value)} className="sp-input" data-testid="sp-audience">
                      <option value="">Select…</option>
                      {AUDIENCE_SIZES.map((s) => (<option key={s} value={s} className="bg-[#0B0B0C]">{s}</option>))}
                    </select>
                  </SpField>
                  <SpField label="Requested topic">
                    <select value={form.topic_id} onChange={(e) => update("topic_id", e.target.value)} className="sp-input" data-testid="sp-topic">
                      <option value="">Any topic / open to suggestions</option>
                      {SPEAKING_TOPICS.map((t) => (<option key={t.id} value={t.id} className="bg-[#0B0B0C]">{t.title}</option>))}
                    </select>
                  </SpField>
                </div>
                <SpField label="Anything else">
                  <textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="sp-input resize-none" placeholder="Format, budget range, audience type, other speakers…" data-testid="sp-message" />
                </SpField>
                {error && <p className="text-red-400 text-sm" data-testid="sp-error">{error}</p>}
                <button type="submit" disabled={submitting} data-testid="sp-submit" className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed">
                  {submitting ? "Sending…" : "Send enquiry"} <ArrowUpRight size={14} />
                </button>
              </form>
            )}
          </div>
        </div>

        <style>{`
          .sp-input {
            width: 100%;
            background: transparent;
            border: 1px solid #343437;
            color: #FAF9F6;
            padding: 0.95rem 1rem;
            font-family: 'Manrope', sans-serif;
            font-size: 0.95rem;
            transition: border-color 300ms ease;
          }
          .sp-input:focus { border-color: #D8A936; outline: none; }
          .sp-input::placeholder { color: #6a6a6a; }
          select.sp-input option { color: #FAF9F6; background: #0B0B0C; }
        `}</style>
      </section>

      {/* Closing */}
      <section className="py-24 border-t border-[#343437]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Also see</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight tracking-tight">
              Every talk is built{" "}<br />on real case studies.
            </h2>
          </div>
          <Link to="/case-studies" data-testid="speaking-cta-cases" className="btn-ghost">
            Explore the work <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}

function SpField({ label, required, children }) {
  return (
    <label className="block">
      <span className="eyebrow block mb-2">
        {label} {required && <span className="text-[#D8A936]">*</span>}
      </span>
      {children}
    </label>
  );
}
